// src/pages/Bookings.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import { Timeline as TimelineIcon, TableRows } from '@mui/icons-material';
import PitchGantt from '../components/PitchGantt';
import axios from 'axios';
import BookingTable from '../components/BookingTable';

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [stops, setStops] = useState([]);
  const [users, setUsers] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'gantt'
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchBookings();
    fetchStops();
    fetchUsers();
    fetchPitches();
  }, []);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admins/bookings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchStops = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admins/stops`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setStops(response.data);
    } catch (error) {
      console.error('Error fetching stops:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admins/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUsers(response.data);
      fetchVehicles();
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchPitches = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admins/restareas`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPitches(response.data);
    } catch (error) {
      console.error('Error fetching pitches:', error);
    }
  };

  // Una sola richiesta per tutti i veicoli. Prima ne partiva una per ogni utente:
  // con oltre 2000 utenti la pagina impiegava una decina di secondi ad aprirsi.
  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admins/vehicles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const handleAdd = async (newBooking) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${apiUrl}/admins/bookings`, newBooking, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setBookings([...bookings, response.data]);
    } catch (error) {
      console.error('Error adding booking:', error);
    }
  };

  /*const handleEdit = async (updatedBooking) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(
        `/api/bookings/${updatedBooking.id}`,
        updatedBooking,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setBookings(
        bookings.map((booking) =>
          booking.id === updatedBooking.id ? response.data : booking
        )
      );
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };*/

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${apiUrl}/admins/bookings/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setBookings(bookings.filter((booking) => booking.id !== id));
    } catch (error) {
      console.error('Error deleting booking:', error);
    }
  };

  // Il server puo' restituire un utente gia' esistente (registrazione da app
  // completata dal backoffice): va aggiornato per id, non accodato.
  const upsertUserInState = (user) => {
    setUsers((prevUsers) =>
      prevUsers.some((u) => u.id === user.id)
        ? prevUsers.map((u) => (u.id === user.id ? { ...u, ...user } : u))
        : [...prevUsers, user]
    );
  };

  const handleAddUser = async (newUser) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${apiUrl}/admins/users`, newUser, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      upsertUserInState(response.data);
      return response.data;
    } catch (error) {
      // 409: il numero appartiene gia' a un cliente con anagrafica compilata.
      // Lo passiamo alla modale, che propone di riusarlo invece di bloccare.
      const existingUser =
        error.response?.status === 409 ? error.response?.data?.user : null;

      if (existingUser) {
        upsertUserInState(existingUser);
        const conflictError = new Error('User already exists');
        conflictError.existingUser = existingUser;
        throw conflictError;
      }

      console.error('Error adding user:', error);
      throw error;
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(
        `${apiUrl}/admins/users/${userId}`,
        userData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      upsertUserInState(response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };

  const handleAddVehicle = async (vehicleData) => {
    try {
      const response = await axios.post(`${apiUrl}/admins/vehicles`, vehicleData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
        },
      });

      // Aggiorna l'array vehicles nello stato per includere il nuovo veicolo
      setVehicles(prevVehicles => {
        const userVehicleIndex = prevVehicles.findIndex(v => v.userId === vehicleData.userId);
        if (userVehicleIndex !== -1) {
          // L'utente ha già veicoli, aggiungi il nuovo alla lista
          const updatedVehicles = [...prevVehicles];
          updatedVehicles[userVehicleIndex] = {
            ...updatedVehicles[userVehicleIndex],
            vehicles: [...updatedVehicles[userVehicleIndex].vehicles, response.data]
          };
          return updatedVehicles;
        } else {
          // L'utente non aveva veicoli, crea una nuova entry
          return [...prevVehicles, { userId: vehicleData.userId, vehicles: [response.data] }];
        }
      });

      return response.data;  // Restituisci il veicolo creato
    } catch (error) {
      console.error('Error adding vehicle:', error);
      throw error;
    }
  };

  // Funzione per aprire il cancello
  const handleOpenGate = async (bookingId) => {
    try {
      const booking = bookings.find(b => b.id === bookingId);
      console.log('DEBUG booking per apertura cancello:', booking);
      if (!booking) throw new Error('Prenotazione non trovata');
      const stopZoneId = booking.stopzonesId || booking.stopzoneId || booking.stopzone_id || booking.stop_zone_id || booking.restareas?.id;
      const stopId = booking.id || booking.stopid || booking.stop_id || booking.stops?.id;
      if (!stopZoneId || !stopId) {
        console.error('DEBUG booking:', booking);
        window.alert(
          `Dati prenotazione insufficienti per aprire il cancello.\nstopZoneId: ${stopZoneId}\nstopId: ${stopId}`
        );
        return;
      }
      const token = localStorage.getItem('adminToken');
      const resp = await axios.post(`${apiUrl}/openGateTest`,
        { stopZoneId, stopId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (resp.data && resp.data.message) {
        window.alert(resp.data.message);
      } else {
        window.alert('Cancello aperto con successo');
      }
    } catch (err) {
      console.error('Errore apertura cancello', err);
      window.alert('Errore apertura cancello');
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Prenotazioni
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.85 }}>
            {viewMode === 'table' ? 'Elenco Prenotazioni' : 'Diagramma Gantt Piazzole'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={viewMode === 'table' ? <TimelineIcon /> : <TableRows />}
            onClick={() => setViewMode(m => m === 'table' ? 'gantt' : 'table')}
          >
            {viewMode === 'table' ? 'Vista Gantt' : 'Vista Tabella'}
          </Button>
        </Box>
        {viewMode === 'table' ? (
          <BookingTable
            bookings={bookings}
            stops={stops}
            getBookings={fetchBookings}
            onAdd={handleAdd}
            //onEdit={handleEdit}
            onDelete={handleDelete}
            onOpenGate={handleOpenGate}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            users={users}
            addUser={handleAddUser}
            updateUser={handleUpdateUser}
            addVehicle={handleAddVehicle}
            pitches={pitches}
            vehicles={vehicles}
            getVehicles={fetchVehicles}
          />
        ) : (
          <PitchGantt bookings={bookings} />
        )}
      </Paper>
    </Box>
  );
};

export default Bookings;
